/*
  Offer test — persistence and measurement plane.
  Companion to snippets/offer-test-head.liquid, which decides the arm before paint
  and exposes window.SIAB_OFFER = { test, arm, vid, bucket }.

  This file:
    1. Stamps the arm onto the Shopify cart as attributes (_offer_test, _offer_vid)
       so it rides onto the order — the only join that reaches Skio survival data
       (policy shirt-in-a-box-bucket-onsite-experiments-via-cart-attribute).
       Stamps on page load when the cart has items, and again after any
       /cart/add.js call (fetch or XHR, which is what the theme's jQuery uses).
    2. Emits offer_test_exposure once per session, add_to_cart and checkout_started
       — to PostHog (funnel diagnostics) and Converge (exposure denominator),
       each carrying test id, arm and vid.
    3. Handles the arm-B cart upgrade block: one click swaps a monthly line onto
       the prepaid selling plan via /cart/change.js.

  Design: companies/shirt-in-a-box/projects/offer-testing-workflow/plan.md §5–6.
  Never decides anything; PostHog/Converge are diagnostics, Shopify is truth.
*/
(function (w, d) {
  'use strict';
  var O = w.SIAB_OFFER;
  if (!O || !O.test) return;
  // Declined consent (or no vid): not in the test. No cart label, no events, no upgrade block
  // (CSS already shows Lane A). Nothing about this visitor is recorded.
  if (!O.inTest || !O.vid) return;

  var ATTR_TEST = '_offer_test', ATTR_VID = '_offer_vid';
  var stampValue = O.test + ':' + O.arm;
  var props = { offer_test: O.test, offer_arm: O.arm, offer_vid: O.vid, offer_bucket: O.bucket, offer_forced: O.forced };

  // ---------- events ----------
  function track(name, extra) {
    var p = Object.assign({}, props, extra || {});
    try { if (w.posthog && typeof w.posthog.capture === 'function') w.posthog.capture(name, p); } catch (e) { console.warn('[offer-test] posthog', e); }
    try { if (typeof w.cvg === 'function') w.cvg({ method: 'track', eventName: name, properties: p }); } catch (e) { console.warn('[offer-test] converge', e); }
  }
  try { if (w.posthog && typeof w.posthog.register === 'function') w.posthog.register({ offer_test: O.test, offer_arm: O.arm, offer_vid: O.vid }); } catch (e) { /* posthog stub not ready; capture() still carries props */ }

  var exposureKey = 'siab_offer_exposed_' + O.test;
  try {
    if (!w.sessionStorage.getItem(exposureKey)) {
      w.sessionStorage.setItem(exposureKey, '1');
      track('offer_test_exposure', { offer_new_visitor: O.newVisitor, path: location.pathname });
    }
  } catch (e) { track('offer_test_exposure', { path: location.pathname, storage_unavailable: true }); }

  // ---------- cart stamping ----------
  var stamping = false, stampedThisLoad = false;
  function stampCart(reason) {
    if (stamping) return Promise.resolve();
    stamping = true;
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.item_count) return null;
        var a = cart.attributes || {};
        if (a[ATTR_TEST] === stampValue && a[ATTR_VID] === O.vid) return cart;
        var attrs = {}; attrs[ATTR_TEST] = stampValue; attrs[ATTR_VID] = O.vid;
        return fetch('/cart/update.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        }).then(function (r) { return r.json(); }).then(function (c) { stampedThisLoad = true; track('offer_test_cart_stamped', { reason: reason }); return c; });
      })
      .catch(function (e) { console.warn('[offer-test] stamp failed', reason, e); })
      .then(function (c) { stamping = false; return c; });
  }

  // Re-stamp after any add-to-cart, whichever transport the theme used.
  var addRe = /\/cart\/add(\.js)?(\?|$)/;
  var _fetch = w.fetch;
  if (_fetch) {
    w.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = _fetch.apply(this, arguments);
      if (addRe.test(url)) p.then(function (r) { if (r && r.ok) { setTimeout(function () { stampCart('fetch-add'); track('add_to_cart', { transport: 'fetch' }); }, 50); } }).catch(function () {});
      return p;
    };
  }
  var _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (typeof url === 'string' && addRe.test(url)) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) { setTimeout(function () { stampCart('xhr-add'); }, 50); track('add_to_cart', { transport: 'xhr' }); }
      });
    }
    return _open.apply(this, arguments);
  };
  // Native form POST to /cart/add (the flagship "filter your box" modal path) lands on
  // /cart with no JS hook, so the page-load stamp below covers it.
  d.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && f.tagName === 'FORM' && /\/cart\/add/.test(f.getAttribute('action') || '')) track('add_to_cart', { transport: 'form' });
  }, true);

  // ---------- checkout started ----------
  d.addEventListener('click', function (e) {
    var el = e.target && e.target.closest && e.target.closest('[name="checkout"], .cart__checkout, a[href^="/checkout"], button[form][name="checkout"]');
    if (el) track('checkout_started', { source: el.className || el.name || 'checkout' });
  }, true);

  // ---------- arm-B cart upgrade: swap a monthly line onto the prepaid plan ----------
  d.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-offer-upgrade]');
    if (!btn) return;
    e.preventDefault();
    if (O.arm !== 'B') return; // block is CSS-hidden for arm A; belt and braces
    var key = btn.getAttribute('data-key');
    var plan = btn.getAttribute('data-selling-plan');
    var qty = parseInt(btn.getAttribute('data-quantity') || '1', 10);
    var variantId = btn.getAttribute('data-variant-id');
    if (!key || !plan) return;

    // A failed swap must be visible. Silently re-enabling the button reads as a dead
    // cart, the shopper clicks again, and the lost session lands on the conversion
    // guardrail this test is being judged on.
    var card = btn.closest('[data-offer-upgrade-block]');
    var errEl = card && card.querySelector('[data-offer-upgrade-error]');
    if (!errEl && card) {
      errEl = d.createElement('p');
      errEl.className = 'offer-upgrade__error';
      errEl.setAttribute('data-offer-upgrade-error', '');
      errEl.setAttribute('role', 'alert');
      card.appendChild(errEl);
    }
    function showError() {
      if (!errEl) return;
      errEl.textContent = 'Something went wrong \u2014 please try again.';
      errEl.hidden = false;
    }
    if (errEl) { errEl.textContent = ''; errEl.hidden = true; }

    btn.disabled = true;
    fetch('/cart/change.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty, selling_plan: plan })
    }).then(function (r) { if (!r.ok) throw new Error('cart/change ' + r.status); return r.json(); })
      .then(function () { track('offer_prepaid_upgrade', { line_key: key, selling_plan: plan, variant_id: variantId }); return stampCart('upgrade'); })
      .then(function () { w.location.reload(); })
      .catch(function (err) {
        console.error('[offer-test] upgrade failed', err);
        track('offer_prepaid_upgrade_failed', { line_key: key, selling_plan: plan, variant_id: variantId, reason: String(err && err.message || err) });
        showError();
        btn.disabled = false;
      });
  });

  // ---------- page-load stamp ----------
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', function () { stampCart('load'); });
  else stampCart('load');
})(window, document);
